import { Loader2, Lock } from "lucide-react";
import { MONTHS, fmtSEK, g } from "../revenue/format";

const DRILLABLE = new Set([
  "receipts_tm", "receipts_projects", "receipts_services", "receipts_other",
  "receipts_pass_through", "overhead_recharge_receipts",
  "employee_payments", "subcontractor_payments", "indirect_payments",
  "factoring_payments", "group_overhead_payments", "pass_through_payments",
  "dividend_payments",
  "vat_settlement", "corporate_tax_payments", "other_cash_items",
]);

function CategoryRow({ label }) {
  return (
    <tr className="bg-secondary/50 border-y border-slate-200" data-testid={`cf-cat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <td colSpan={14} className="py-1.5 px-3 text-[11px] font-bold uppercase tracking-widest text-slate-500 sticky left-0 bg-slate-50">
        {label}
      </td>
    </tr>
  );
}

function CashRow({ line, label, data, kind = "line", onDrilldown }) {
  const strong = kind === "subtotal" || kind === "final";
  const final = kind === "final";
  const drillable = DRILLABLE.has(line);
  const renderVal = (val) => (val == null ? "—" : fmtSEK(val));
  return (
    <tr className={`border-b border-slate-100 ${final ? "bg-[#F8FAFC] border-t-2 border-t-slate-300" : strong ? "bg-slate-50/50" : ""}`} data-testid={`cf-row-${line}`}>
      <td className="py-1.5 px-3 sticky left-0 z-10" style={{ backgroundColor: final ? "#F8FAFC" : strong ? "#FAFBFC" : "#fff" }}>
        {drillable ? (
          <button
            className={`text-left text-sm hover:underline ${strong ? "font-bold text-slate-900" : "text-[#0052CC]"}`}
            onClick={() => onDrilldown(line, 1)}
            data-testid={`cf-open-${line}`}
          >
            {label}
          </button>
        ) : (
          <span className={`text-sm ${strong ? "font-bold text-slate-900" : "text-slate-700"}`}>{label}</span>
        )}
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const cell = g(data.months, m) || {};
        const val = cell[line];
        return (
          <td key={m} className={`py-1.5 px-1 text-right ${cell.closed ? "bg-slate-100/70" : ""}`} data-testid={`cf-cell-${line}-${m}`}>
            <button
              className={`h-8 w-full pr-1.5 text-right text-sm ${strong ? "font-bold text-slate-900" : "text-slate-700"} ${val != null && val < 0 ? "text-[#B91C1C]" : ""} ${drillable ? "hover:bg-secondary/50 rounded" : "cursor-default"}`}
              onClick={drillable ? () => onDrilldown(line, m) : undefined}
              data-testid={`cf-cell-${line}-${m}-btn`}
            >
              {renderVal(val)}
            </button>
          </td>
        );
      })}
      <td className={`py-1.5 px-2 text-right text-sm font-bold ${final ? "text-[#0052CC]" : "text-slate-900"}`} data-testid={`cf-fy-${line}`}>
        {renderVal(data.fy[line])}
      </td>
    </tr>
  );
}

export default function CashFlowGrid({ data, loading, onDrilldown }) {
  // Build 8 hub lines render only when they carry values — standalone/operating
  // companies keep the canonical layout unchanged.
  const hasValues = (line) =>
    data && (Object.values(data.months || {}).some((m) => (m[line] || 0) !== 0) || (data.fy?.[line] || 0) !== 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="cf-grid">
      {loading && (
        <div className="py-16 flex justify-center" data-testid="cf-loading">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      )}
      {!loading && data && (
        <table className="w-full min-w-[1250px] border-collapse">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3 sticky left-0 bg-secondary/50 min-w-56 z-10">Cash Flow</th>
              {MONTHS.map((label, i) => {
                const closed = g(data.periods, i + 1)?.status === "CLOSED";
                return (
                  <th key={label} className={`text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-1 w-24 ${closed ? "text-slate-400 bg-slate-100/70" : "text-slate-500"}`}>
                    <div className="flex items-center justify-end gap-1 pr-1.5">
                      {closed && <Lock className="h-3 w-3" />}
                      {label}
                    </div>
                  </th>
                );
              })}
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-28">FY</th>
            </tr>
          </thead>
          <tbody>
            <CashRow line="opening" label="Opening Cash" kind="subtotal" data={data} onDrilldown={onDrilldown} />
            <CategoryRow label="Operating Cash Inflows" />
            <CashRow line="receipts_tm" label="Customer Receipts — T&M" data={data} onDrilldown={onDrilldown} />
            <CashRow line="receipts_projects" label="Customer Receipts — Projects" data={data} onDrilldown={onDrilldown} />
            <CashRow line="receipts_services" label="Customer Receipts — Services" data={data} onDrilldown={onDrilldown} />
            <CashRow line="receipts_other" label="Customer Receipts — Other" data={data} onDrilldown={onDrilldown} />
            {hasValues("receipts_pass_through") && (
              <CashRow line="receipts_pass_through" label="Customer Receipts — Pass-through" data={data} onDrilldown={onDrilldown} />
            )}
            {hasValues("overhead_recharge_receipts") && (
              <CashRow line="overhead_recharge_receipts" label="Group Overhead Recharge Receipts" data={data} onDrilldown={onDrilldown} />
            )}
            <CashRow line="total_inflows" label="Total Inflows" kind="subtotal" data={data} onDrilldown={onDrilldown} />
            <CategoryRow label="Operating Cash Outflows" />
            <CashRow line="employee_payments" label="Employee-related Payments" data={data} onDrilldown={onDrilldown} />
            <CashRow line="subcontractor_payments" label="Subcontractor Payments" data={data} onDrilldown={onDrilldown} />
            <CashRow line="indirect_payments" label="Indirect Cost Payments" data={data} onDrilldown={onDrilldown} />
            <CashRow line="factoring_payments" label="Factoring Payments" data={data} onDrilldown={onDrilldown} />
            <CashRow line="group_overhead_payments" label="Group Overhead Payments" data={data} onDrilldown={onDrilldown} />
            {hasValues("pass_through_payments") && (
              <CashRow line="pass_through_payments" label="Pass-through Payments to Operating Cos" data={data} onDrilldown={onDrilldown} />
            )}
            <CashRow line="total_outflows" label="Total Outflows" kind="subtotal" data={data} onDrilldown={onDrilldown} />
            {hasValues("dividend_payments") && (
              <>
                <CategoryRow label="Dividends" />
                <CashRow line="dividend_payments" label="Dividend Payments" data={data} onDrilldown={onDrilldown} />
              </>
            )}
            <CategoryRow label="Tax / VAT" />
            <CashRow line="vat_settlement" label="VAT Settlement" data={data} onDrilldown={onDrilldown} />
            <CashRow line="corporate_tax_payments" label="Corporate Tax Payments" data={data} onDrilldown={onDrilldown} />
            <CashRow line="other_cash_items" label="Other Cash Items" data={data} onDrilldown={onDrilldown} />
            <CashRow line="net_cash_flow" label="Net Cash Flow" kind="subtotal" data={data} onDrilldown={onDrilldown} />
            <CashRow line="closing" label="Closing Cash" kind="final" data={data} onDrilldown={onDrilldown} />
          </tbody>
        </table>
      )}
    </div>
  );
}
